import { Injectable } from '@angular/core';

import { PicksView } from '@cds/betting-offer/domain-specific';
import { PicksRequest } from '@cds/query-objects';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { Action } from '@ngrx/store';
import { groupBy } from 'lodash-es';
import { Observable, filter, merge, mergeMap } from 'rxjs';

import { BetslipType } from '../../../core/betslip-type';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BaseBetslipGroupPick, BetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { PickId } from '../../../core/picks/pick-id';
import { PickOddsState } from '../../../core/picks/pick-models';
import BetbuilderActions, { IRequestBetBuilderPricePayload } from '../../bet-builder/actions';
import { IBetslipTypeState } from '../../types/state';
import BetslipPickActions from '../actions';
import BetslipPickFactory from '../factory/betslip-pick.factory';
import { replaceGroup } from '../grouped-picks.actions';
import { BetslipPicksRequestBuilderService } from './betslip-pick-request-builder.service';
import { GroupablePicks } from './grouped-pick/groupable-picks.model';
import { GroupedPicksService } from './grouped-pick/grouped-picks.service';

@Injectable()
export default class PickRefresherService {
    constructor(
        private pickRequestBuilder: BetslipPicksRequestBuilderService,
        private pickFactory: BetslipPickFactory,
        private bettingOfferApi: BettingOfferApi,
        private groupedPicksService: GroupedPicksService,
        private betslipConfig: BetslipConfig,
    ) {}

    refreshPicks(pickLists: BetslipPick[], betslipTypeState: IBetslipTypeState, selectedType: BetslipType | null): Observable<Action> {
        const picks = pickLists.filter((pick) => !BaseBetslipGroupPick.isPick(pick));
        const { classicPicks, entainUiBetBuilderPicks } = this.groupPicks(picks);
        const self = this;
        let availableGroups: GroupablePicks[] = [];
        if (selectedType === BetslipType.Combo) {
            availableGroups = this.groupedPicksService.getAvailablePicksForGrouping(classicPicks);
            // this.stripGroupInfoFromInvalidPicks(availableGroups);
        }

        const request = this.pickRequestBuilder.buildRequestFromPicks(picks, true);
        this.updatePickGroupIdForNonGroupablePicks(request, availableGroups);
        const updateEntainUiBetBuilderPicks = entainUiBetBuilderPicks.map((bbPick) => {
            const stake = this.getPickStake(betslipTypeState, bbPick.id.toString());

            const updateBetBuilderPayload: IRequestBetBuilderPricePayload = {
                eventId: bbPick.eventId,
                pickId: bbPick.id,
                sportcastOptions: bbPick.sportcastOptions,
                sportId: bbPick.sportId,
                externalFixtureId: bbPick.sportcastId!,
                betgeniusId: Number(bbPick.betgeniusId),
                stake,
                isInit: true,
                useV2Key: !!bbPick.useV2Key,
            };

            return BetbuilderActions.requestBetBuilderPrice({ payload: updateBetBuilderPayload });
        });

        const updateClassicPicks = this.bettingOfferApi.getPicksViewCache(request).pipe(
            filter((response) => !!response?.fixturePage?.fixtures),
            mergeMap(function* (response: PicksView | undefined): Iterable<Action> {
                const pickIdsToRemove: PickId[] = [];
                for (const classicPick of classicPicks) {
                    const fixture = response!.fixturePage.fixtures.find((current) => current.id === classicPick.id.fixtureId);

                    let updatedPick = fixture ? self.pickFactory.createNewPickFromOldAndBcdPick(classicPick, classicPick.id, fixture) : null;

                    if (!updatedPick || self.isOddStateClosed(updatedPick)) {
                        pickIdsToRemove.push(classicPick.id);
                        continue;
                    }

                    const group = availableGroups.find((t) => t.picks.some((p) => p.id.toString() === classicPick.id.toString()));
                    if (group) {
                        group.picks = [...group.picks.filter((p) => p.id.toString() !== classicPick.id.toString()), updatedPick];
                    }

                    yield BetslipPickActions.replacePick({ pickId: classicPick.id, newPick: updatedPick });
                }

                if (pickIdsToRemove.length) {
                    yield BetslipPickActions.requestRemoveMultiplePicks({ pickIds: pickIdsToRemove });
                }

                const groupPickIdsToRemove: PickId[][] = availableGroups
                    .map((group) => group.picks.filter((pick) => pickIdsToRemove.some((p) => p === pick.id)).map((pick) => pick.id))
                    .filter((closedPickIds) => closedPickIds.length);

                for (const idsToRemove of groupPickIdsToRemove) {
                    yield BetslipPickActions.requestRemovePicksFromGroup({ pickIds: idsToRemove });
                }

                if (Object.values(response!.betBuilderPricingGroups).length) {
                    const groupPicks = self.groupedPicksService.buildGroupsFromPicksView(availableGroups, response!.betBuilderPricingGroups);
                    for (const groupPick of groupPicks) {
                        yield replaceGroup({ pickId: groupPick.id, newPick: groupPick });
                    }
                }
                // yield BetslipPickActions.picksRefreshed();
            }),
        );

        return merge(updateClassicPicks, updateEntainUiBetBuilderPicks);
    }

    groupPicks(picks: BetslipPick[]) {
        const pickGroups: { classicPicks: BetslipPick[]; entainUiBetBuilderPicks: BetslipBetBuilderPick[] } = {
            classicPicks: [],
            entainUiBetBuilderPicks: [],
        };

        return picks.reduce<typeof pickGroups>((groups, pick) => {
            if (BetslipBetBuilderPick.isEntainUiBetBuilderPick(pick)) {
                groups.entainUiBetBuilderPicks.push(pick);
            } else {
                groups.classicPicks.push(pick);
            }

            return groups;
        }, pickGroups);
    }

    //This function will remove group info from cds request payload to avoid requesting group price for invalid groups
    private updatePickGroupIdForNonGroupablePicks(request: PicksRequest, availableGroups: GroupablePicks[]) {
        const groupableEntities: { pickGroupId?: string }[] = (request.tv1Picks ?? []).concat(request.tv2Picks ?? []);
        const pickGroups = groupBy(groupableEntities, (g) => g.pickGroupId);
        for (const pickGroupId in pickGroups) {
            if (!availableGroups.find((g) => g.groupInfo.groupId === pickGroupId)?.isValid) {
                pickGroups[pickGroupId].forEach((g) => delete g.pickGroupId);
            }
        }
    }

    private getPickStake(typeState: IBetslipTypeState, pickId: string): number {
        return (this.betslipConfig.isSportcastAsComboEnabled ? typeState.comboBet.stake : typeState.singleBet.picks[pickId].stake) ?? 0;
    }

    private isOddStateClosed(pick: BetslipGroupPick | BetslipPick) {
        return pick.oddsState === PickOddsState.Closed;
    }
}
